<!--
  The live Promotions page: the shared PromotionsPage with the API behind it.

  Everything visible lives in components/promotions/PromotionsPage.vue, which the demo
  store mounts too — one copy, so the two can never drift. This file fetches, writes
  and handles the states a demo never has: loading, failure, and a company with no
  product zones to promote in yet.
-->
<template>
  <Section v-if="error" :title="$t('promos.title')" margin-top="20px">
    <div class="promo-state promo-state-bad">
      <i class="bi bi-exclamation-triangle"></i>
      <div>
        <b>{{ $t('promos.view.loadFailed') }}</b>
        <p>{{ error }}</p>
        <button type="button" class="btn-primary" @click="load">{{ $t('promos.view.tryAgain') }}</button>
      </div>
    </div>
  </Section>

  <!-- The only blocking state left. A counting line CAN carry a promo — it just
       cannot see who stopped — so the page is withheld solely when there is no
       product zone at all. -->
  <Section v-else-if="ready && !zones.length" :title="$t('promos.title')" margin-top="20px">
    <p class="pro-sub">{{ $t('promos.sub') }}</p>
    <div class="promo-state">
      <i class="bi bi-tag"></i>
      <div>
        <b>{{ $t('promos.view.noZones') }}</b>
        <p>
          {{ $t('promos.view.noZonesBody') }}
        </p>
      </div>
    </div>
  </Section>

  <PromotionsPage
    v-else
    :promos="promos"
    :zones="zones"
    :coverage-options="COVERAGE_OPTIONS"
    :counts-only-zones="countsOnly"
    :prior-promo="priorPromo"
    :readiness="readiness"
    :can-edit="isAdmin"
    :busy="busy"
    @draft-change="onDraft"
    @log="onLog"
  />
</template>

<script setup>
import { computed, onMounted, ref, shallowRef } from 'vue'
import { useI18n } from 'vue-i18n'
import PromotionsPage from '@/components/promotions/PromotionsPage.vue'
import Section from '@/components/ui/Section.vue'
import { useCompanyStore } from '@/stores/CompanyStore'
import { useUserStore } from '@/stores/UserStore'

const store = useCompanyStore()
const userStore = useUserStore()
const { t } = useI18n()

const promos = ref([])
const zones = ref([])
/**
 * Product zones drawn as a counting line. They carry a promo perfectly well — the
 * euro comes off the till, and reach and zone traffic are what a line counts — they
 * simply never see who stopped. Kept so the form can say which is which.
 */
const countsOnly = ref([])
const readiness = shallowRef(null)
const priorPromo = shallowRef(null)
const error = ref('')
const busy = ref(false)
const ready = ref(false)

/** Writes are admin-only on the server; the page hides what a reader cannot do. */
const isAdmin = computed(() => !!userStore.isAdminInCurrentCompany)

/**
 * Declared at logging time, never measured — it only ever produces a warning.
 * Mirrors coach/promo.COVERAGE, and the copy lives there because the warning the
 * server returns has to agree with the choice the form offered.
 */
const COVERAGE_OPTIONS = computed(() => ['few_items', 'section', 'most_of_aisle']
  .map((key) => ({ key, label: t(`promos.view.coverage.${key}`) })))

/**
 * The list carries each promo's measurement, because the overview shows a euro and a
 * verdict per row. It is computed on request rather than precomputed — but in ONE
 * request for the whole list (`?measure=1`), not one per promo on top of the list,
 * which is what this used to do.
 */
async function load() {
  error.value = ''
  busy.value = true
  try {
    const [rows, zs] = await Promise.all([
      store.fetchPromos({ measure: true }), store.fetchPromoZones(),
    ])
    zones.value = zs.eligible
    countsOnly.value = zs.countsOnly
    promos.value = rows.map((p) => ({
      ...p,
      Data: typeof p.Data === 'string' ? JSON.parse(p.Data || '{}') : (p.Data || {}),
      measurement: p.measurement || {},
    }))
  } catch (e) {
    error.value = e?.response?.data?.error || e?.message || t('promos.view.unknownError')
  } finally {
    busy.value = false
    ready.value = true
  }
}

/**
 * The form announces its draft on every change and we answer with readiness and with
 * what was said about the last promo in that zone. A failure here is not worth an
 * error banner: the form still works, it just loses its live feedback.
 */
let draftSeq = 0
async function onDraft(d) {
  priorPromo.value = lastPromoIn(d.zone)
  if (!d.zone || !d.start || !d.end) {
    readiness.value = null
    return
  }
  const seq = ++draftSeq
  try {
    const r = await store.fetchPromoReadiness({
      zone: d.zone, start: d.start, end: d.end, coverage: d.coverage,
    })
    if (seq === draftSeq) readiness.value = r
  } catch {
    if (seq === draftSeq) readiness.value = null
  }
}

/** The most recent concluded promo in a zone, for the "last time you did this" line. */
function lastPromoIn(zone) {
  if (!zone) return null
  const past = promos.value
    .filter((p) => p.Data.zoneName === zone && p.Data.outcome?.repeat)
    .sort((a, b) => String(b.EndDate).localeCompare(String(a.EndDate)))
  return past[0] || null
}

async function onLog(payload) {
  busy.value = true
  try {
    await store.createPromo(payload)
    await load()
  } catch (e) {
    error.value = e?.response?.data?.error || e?.message || t('promos.view.saveFailed')
  } finally {
    busy.value = false
  }
}

onMounted(load)
</script>

<style scoped>
.pro-sub { margin: 0 0 16px; color: #6b7a87; font-size: 0.92rem; }
.promo-state {
  display: flex; gap: 14px; max-width: 680px;
  margin: 4px 0 0; padding: 18px 20px;
  background: #fff; border: 1px solid var(--stora-border, #e6edf3); border-radius: 12px;
  color: #425461; font-size: 0.9rem; line-height: 1.55;
}
.promo-state > i { font-size: 1.2rem; color: #93a1ad; }
.promo-state b { display: block; margin-bottom: 4px; color: #1d2b3a; }
.promo-state p { margin: 0 0 10px; }
.promo-state p:last-child { margin-bottom: 0; }
.promo-state b { font-weight: 700; }
.promo-state-bad { background: #fdf6f5; border-color: #f0d9d6; }
.promo-state-bad > i { color: #b3372f; }
.btn-primary {
  padding: 7px 15px; border-radius: 7px; border: none; cursor: pointer;
  background: var(--stora-accent, #4F7EB3); color: #fff;
  font-size: 13px; font-weight: 600;
}
</style>
